import gsap from 'gsap'
import { GLContext } from 'nanogl/types'
import Node from 'nanogl-gltf/lib/elements/Node'
import MaterialOverrideExtension from 'nanogl-gltf/lib/extensions/MaterialOverrideExtension'
import Gltf from 'nanogl-gltf/lib/Gltf'
import UnlitPass from 'nanogl-pbr/UnlitPass'

import { RenderContext } from '@/webgl/core/RenderContext'
import { clamp01, map } from '@/webgl/math'
import GltfResource from '@/webgl/resources/GltfResource'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import ILoadable from '@/webgl/runner/interfaces/ILoadable'
import { backgroundStencil } from '@/webgl/runner/rendering/RenderingUtils'

export default class EyeMaskRenderer implements ILoadable {
  world: GameWorld
  gl: GLContext
  gltf: Gltf

  node: Node
  weights: Float32Array

  public enableBlinking: boolean = true
  public closeFactor: number = 0

  private closed: number = 0
  public wide: number = 0

  time: number = 0

  public constructor(gl: GLContext, world: GameWorld) {
    this.gl = gl
    this.node = new Node()
    this.world = world

    this.world.root.add(this.node)

    // const fld = gui.folder('Eye');
    // fld.add(this, 'wide', { min: 0, max: 1 });
  }

  public async load(abort: AbortSignal): Promise<void> {
    const overrides: MaterialOverrideExtension = new MaterialOverrideExtension()

    overrides.overridePass('eye', (ctx, m) => {
      const passInstance = m.getPass('color')
      const pass = passInstance.pass as UnlitPass
      pass.glconfig.enableDepthTest(false)
      backgroundStencil(this.gl, pass)
      return passInstance.pass
    })

    this.gltf = await new GltfResource('common/eye.glb', this.gl, { extensions: [overrides] }).load()

    this.node.add(this.gltf.root)

    for (const renderable of this.gltf.renderables) {
      renderable.node.setScale(1.55)
      this.weights = renderable.node.weights
    }
  }

  setScale(value: number) {
    for (const renderable of this.gltf.renderables) {
      renderable.node.setScale(value)
    }
  }

  close(duration: number = 1, delay: number = 0): Promise<void> {
    return new Promise((resolve) => {
      this.enableBlinking = false
      gsap.to(this, {
        closeFactor: 1,
        duration,
        ease: 'power2.inOut',
        delay,
        onComplete: resolve,
      })
    })
  }

  open(duration: number = 1, delay: number = 0): Promise<void> {
    return new Promise((resolve) => {
      this.enableBlinking = false
      gsap.to(this, {
        closeFactor: 0,
        duration,
        ease: 'power2.inOut',
        delay,
        onComplete: () => {
          this.enableBlinking = true
          resolve()
        },
      })
    })
  }

  public preRender(time: TimeEntity) {
    this.time += time.deltaTime * (this.enableBlinking ? 1 : 0)

    // Close
    let phase1 = Math.max(0, Math.sin(this.time * 1.2))
    phase1 = clamp01(map(phase1, 0.95, 1, 0, 1))

    // blink
    const phase2 = (Math.sin(this.time * 4) + 1) * 0.05

    this.closed = Math.max(0, (phase2 + phase1) * 1.0 - this.closeFactor)
    this.closed = clamp01(this.closed + this.closeFactor)
    this.weights[0] = this.closed
    this.weights[1] = this.wide

    this.node.x += (this.world.player.node.x - this.node.x) * time.deltaTime * 0.6

    // this.node.z = -this.world.settings.laneLength;
    // this.node.y = 0.0;
    // this.node.lookAt(this.world.player.node.position);
    // this.node.rotateY(Math.PI);
  }

  public render(context: RenderContext) {
    for (const renderable of this.gltf.renderables) {
      renderable.render(context.gl, context.camera, context.mask, context.pass, context.glConfig)
    }
  }
}
